import { useEffect, useMemo, useState } from 'preact/hooks';
import {
  COUNTRY_CODES,
  PROFILE_MAX_LENGTH,
  validateProfile,
  type ProfileDetails,
  type ProfileField,
} from '@chat-widget/shared/src/profile';
import { ErrorNote, Loading } from './Dashboard';
import { dashboardApi, type Profile, type ProfileChange } from './dashboardApi';

// ============================================================================
// Profile — /app/profile. The account's details: what came from Google
// sign-in (the email, which can't be changed here — see
// shared/src/profile.ts) and what the customer adds themselves. Every
// saved change is kept in profile_changes on the server; the most recent
// are listed at the bottom.

/** Tells the site header to re-read who is signed in, so a new name shows
 * in the account menu at once (Site.tsx's useCurrentUser). */
export const PROFILE_UPDATED_EVENT = 'volgachat:profile-updated';

const FIELD_LABELS: Record<ProfileField, string> = {
  name: 'Name',
  company: 'Company',
  phone: 'Phone',
  website: 'Website',
  country: 'Country',
};

const PROVIDER_LABELS: Record<string, string> = { google: 'Google' };

let regionNames: Intl.DisplayNames | null | undefined;
function countryName(code: string): string {
  if (regionNames === undefined) {
    try {
      regionNames = new Intl.DisplayNames(undefined, { type: 'region' });
    } catch {
      regionNames = null; // an old browser: the code itself still says it
    }
  }
  return regionNames?.of(code) ?? code;
}

function fmtDay(ms: number): string {
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

function fmtWhen(ms: number): string {
  return new Date(ms).toLocaleString(undefined, { year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
}

function shown(field: ProfileField, value: string | null): string {
  if (!value) return 'empty';
  return field === 'country' ? countryName(value) : value;
}

type Form = Record<ProfileField, string>;

function toForm(p: ProfileDetails): Form {
  return { name: p.name, company: p.company ?? '', phone: p.phone ?? '', website: p.website ?? '', country: p.country ?? '' };
}

function History({ changes }: { changes: ProfileChange[] }) {
  if (changes.length === 0) return null;
  return (
    <div class="db-profile-history">
      <h2 class="db-h2">Recent changes</h2>
      <ul>
        {changes.map((c, i) => (
          <li key={`${c.changedAt}-${c.field}-${i}`}>
            <span class="db-muted">{fmtWhen(c.changedAt)}</span>
            <span>
              <strong>{FIELD_LABELS[c.field] ?? c.field}</strong>: {shown(c.field, c.oldValue)} → {shown(c.field, c.newValue)}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

export function ProfilePage() {
  const [profile, setProfile] = useState<Profile | null>(null);
  const [changes, setChanges] = useState<ProfileChange[]>([]);
  const [form, setForm] = useState<Form | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    dashboardApi.getProfile().then(
      (res) => {
        setProfile(res.profile);
        setChanges(res.changes);
        setForm(toForm(res.profile));
      },
      (err) => setLoadError(err instanceof Error ? err.message : "We couldn't load your profile."),
    );
  }, []);

  const countries = useMemo(
    () => COUNTRY_CODES.map((code) => ({ code, name: countryName(code) })).sort((a, b) => a.name.localeCompare(b.name)),
    [],
  );

  if (!profile || !form) {
    return (
      <section class="site-section db-page">
        <div class="site-wrap">{loadError ? <ErrorNote error={loadError} /> : <Loading label="Loading your profile…" />}</div>
      </section>
    );
  }

  const dirty = (Object.keys(form) as ProfileField[]).some((f) => form[f] !== toForm(profile)[f]);
  const set = (field: ProfileField) => (e: Event) => {
    setSaved(null);
    // From the latest form, not this render's: a browser's autofill sets
    // several fields before the page re-renders, and each would otherwise
    // undo the one before.
    const value = (e.target as HTMLInputElement | HTMLSelectElement).value;
    setForm((current) => current && { ...current, [field]: value });
  };

  async function save(e: Event) {
    e.preventDefault();
    if (!form) return;
    setError(null);
    setSaved(null);
    const checked = validateProfile(form);
    if (!checked.ok) {
      setError(checked.error);
      return;
    }
    setBusy(true);
    try {
      const res = await dashboardApi.updateProfile(checked.value);
      setProfile(res.profile);
      setChanges(res.changes);
      setForm(toForm(res.profile));
      setSaved(res.changed.length ? 'Saved.' : 'Nothing had changed.');
      if (res.changed.includes('name')) window.dispatchEvent(new Event(PROFILE_UPDATED_EVENT));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t save — please try again.');
    } finally {
      setBusy(false);
    }
  }

  const signIn = profile.signInWith.map((p) => PROVIDER_LABELS[p] ?? p).join(', ') || 'Google';

  return (
    <section class="site-section db-page">
      <div class="site-wrap">
        <nav class="db-subnav">
          <a href="/app" class="db-subnav-back">
            Back to bots
          </a>
        </nav>
        <h1>Profile</h1>
        <p class="db-sub">Your account details. Only you and the VolgaChat team see them.</p>

        <dl class="db-profile-facts">
          <div>
            <dt>Email</dt>
            <dd>
              {profile.email}
              <span class="db-muted db-field-note">From your {signIn} sign-in, so it can't be changed here.</span>
            </dd>
          </div>
          <div>
            <dt>Signs in with</dt>
            <dd>{signIn}</dd>
          </div>
          <div>
            <dt>Member since</dt>
            <dd>{fmtDay(profile.createdAt)}</dd>
          </div>
        </dl>

        <form class="db-stack-form" onSubmit={save} noValidate>
          <label class="db-field">
            <span>Name</span>
            <input class="db-input" value={form.name} maxLength={PROFILE_MAX_LENGTH.name} autoComplete="name" onInput={set('name')} />
          </label>
          <label class="db-field">
            <span>
              Company <span class="db-field-optional">optional</span>
            </span>
            <input
              class="db-input"
              value={form.company}
              maxLength={PROFILE_MAX_LENGTH.company}
              autoComplete="organization"
              onInput={set('company')}
            />
          </label>
          <label class="db-field">
            <span>
              Phone <span class="db-field-optional">optional</span>
            </span>
            <input
              class="db-input"
              type="tel"
              value={form.phone}
              maxLength={PROFILE_MAX_LENGTH.phone}
              placeholder="+1 555 123 4567"
              autoComplete="tel"
              onInput={set('phone')}
            />
          </label>
          <label class="db-field">
            <span>
              Website <span class="db-field-optional">optional</span>
            </span>
            <input
              class="db-input"
              type="url"
              value={form.website}
              maxLength={PROFILE_MAX_LENGTH.website}
              placeholder="https://example.com"
              autoComplete="url"
              onInput={set('website')}
            />
          </label>
          <label class="db-field">
            <span>
              Country <span class="db-field-optional">optional</span>
            </span>
            <select class="db-input db-select" value={form.country} autoComplete="country" onChange={set('country')}>
              <option value="">Not set</option>
              {countries.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </label>
          <ErrorNote error={error} />
          {saved && <p class="db-success">{saved}</p>}
          <div>
            <button type="submit" class="site-btn site-btn-primary" disabled={busy || !dirty}>
              {busy ? 'Saving…' : 'Save changes'}
            </button>
          </div>
        </form>

        <History changes={changes} />
      </div>
    </section>
  );
}
